// StateRevenueMap — US state choropleth shaded by revenue (brand-green scale).
// Uses us-atlas state boundaries + d3-geo AlbersUsa; hover tooltip + gradient legend.

import { useEffect, useMemo, useRef, useState } from 'react'
import { geoAlbersUsa, geoPath } from 'd3-geo'
import { feature } from 'topojson-client'

const US_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json'
let _usCache: any = null

interface StateRow { state: string; revenue: number; orders?: number }

const fmt$ = (n: number): string => {
  const a = Math.abs(n)
  if (a >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (a >= 1_000)     return `$${(n / 1_000).toFixed(0)}k`
  return `$${Math.round(n)}`
}

// light mint -> deep brand green, on sqrt to spread the concentrated top states
function shade(t: number): string {
  const c = Math.max(0, Math.min(1, Math.sqrt(t)))
  const l = (a: number, b: number) => Math.round(a + (b - a) * c)
  return `rgb(${l(0xED, 0x00)},${l(0xF8, 0x86)},${l(0xF1, 0x4B)})`  // #EDF8F1 -> #00864B
}

export function StateRevenueMap({ data, height = 380 }: { data: StateRow[]; height?: number }) {
  const [us, setUs] = useState<any>(null)
  const [hover, setHover] = useState<{ x: number; y: number; name: string; val: number; orders?: number } | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      if (!_usCache) _usCache = await (await fetch(US_URL)).json()
      if (alive) setUs(_usCache)
    })()
    return () => { alive = false }
  }, [])

  const byState = useMemo(() => {
    const m = new Map<string, StateRow>()
    data.forEach((d) => m.set((d.state || '').toUpperCase(), d))
    return m
  }, [data])
  const maxRev = useMemo(() => Math.max(1, ...data.map((d) => d.revenue)), [data])

  const { features, pathFor } = useMemo(() => {
    if (!us) return { features: [] as any[], pathFor: (_: any) => '' }
    const fc: any = feature(us, us.objects.states)
    const proj = geoAlbersUsa().fitSize([960, 600], fc)
    const gp = geoPath(proj)
    return { features: fc.features as any[], pathFor: (f: any) => gp(f) || '' }
  }, [us])

  const nameOf = (f: any) => f?.properties?.name || f?.properties?.NAME || ''

  if (!us) return <div className="cax-load" style={{ padding: 24 }}>Loading map…</div>

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <svg viewBox="0 0 960 600" width="100%" height={height} style={{ display: 'block' }}
           onMouseLeave={() => setHover(null)}>
        {features.map((f, i) => {
          const row = byState.get(nameOf(f).toUpperCase())
          const rev = row?.revenue || 0
          const fill = row ? shade(rev / maxRev) : '#F1F4F8'
          return (
            <path key={i} d={pathFor(f)} fill={fill}
              stroke="rgba(255,255,255,0.7)" strokeWidth={0.6} strokeLinejoin="round"
              style={{ transition: 'fill .15s', cursor: row ? 'pointer' : 'default' }}
              onMouseEnter={(e) => {
                const r = wrapRef.current?.getBoundingClientRect()
                setHover({ x: e.clientX - (r?.left || 0), y: e.clientY - (r?.top || 0),
                           name: nameOf(f), val: rev, orders: row?.orders })
                ;(e.target as SVGPathElement).setAttribute('stroke', '#0F172A')
                ;(e.target as SVGPathElement).setAttribute('stroke-width', '1.2')
              }}
              onMouseMove={(e) => {
                const r = wrapRef.current?.getBoundingClientRect()
                setHover((h) => h && { ...h, x: e.clientX - (r?.left || 0), y: e.clientY - (r?.top || 0) })
              }}
              onMouseLeave={(e) => {
                ;(e.target as SVGPathElement).setAttribute('stroke', 'rgba(255,255,255,0.7)')
                ;(e.target as SVGPathElement).setAttribute('stroke-width', '0.6')
              }}
            />
          )
        })}
      </svg>

      {/* gradient legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
        <span>Lower</span>
        <div style={{ flex: 'none', width: 140, height: 8, borderRadius: 4,
                      background: 'linear-gradient(90deg, #EDF8F1, #00864B)' }} />
        <span>Higher · {fmt$(maxRev)} top state</span>
      </div>

      {hover && (
        <div style={{
          position: 'absolute', left: Math.min(hover.x + 14, (wrapRef.current?.clientWidth || 800) - 170),
          top: hover.y + 14, pointerEvents: 'none', zIndex: 5,
          background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 8,
          padding: '7px 11px', fontSize: 12, color: 'var(--text-main)', boxShadow: '0 6px 20px rgba(15,23,42,0.18)',
        }}>
          <div style={{ fontWeight: 600 }}>{hover.name}</div>
          <div style={{ color: 'var(--text-muted)' }}>
            {fmt$(hover.val)}{hover.orders != null ? ` · ${hover.orders.toLocaleString()} orders` : ''}
          </div>
        </div>
      )}
    </div>
  )
}
